import {
  timestampFormatter,
  TIMESTAMP_MINUTE_FORMAT,
} from "../../../core/utils/timestampFormatter";

/**
 * Human-readable label for the last confirmed CRM write.
 *
 * @example
 * formatCrmWrittenStatusLabel("2026-07-24T10:30:00Z");
 * // "Last write Jul 24, 2026 4:00 PM"
 */
export const formatCrmWrittenStatusLabel = (timestamp) => {
  const formatted = timestampFormatter(timestamp, TIMESTAMP_MINUTE_FORMAT);
  if (!formatted || formatted === "Invalid Date") {
    return "";
  }
  return `Last write ${formatted}`;
};

/**
 * Catalog/detail sync-column label for a CRM write-back connector.
 *
 * @example
 * getCrmSyncDisplayLabel({ connected: true, last_written_at: "2026-07-24T10:30:00Z" });
 * // "Last write Jul 24, 2026 4:00 PM"
 *
 * @example
 * getCrmSyncDisplayLabel({ connected: true, writes_count: 0 });
 * // "Write-back ready"
 */
export const getCrmSyncDisplayLabel = (status = {}) => {
  if (!status?.connected) {
    return "—";
  }
  if (status.last_written_at) {
    return formatCrmWrittenStatusLabel(status.last_written_at) || "Write-back ready";
  }
  return "Write-back ready";
};

/**
 * Detail header meta for a connected CRM connector.
 *
 * @example
 * getCrmDetailHeaderMeta({ connected: true, writes_count: 3, last_written_at: "…" });
 * // "3 writes · Last write Jul 24, 2026 4:00 PM"
 */
export const getCrmDetailHeaderMeta = (status = {}) => {
  if (!status?.connected) {
    return "Not connected";
  }
  const writesCount = Number(status.writes_count) || 0;
  const lastWrite = status.last_written_at
    ? formatCrmWrittenStatusLabel(status.last_written_at)
    : "";
  if (writesCount > 0 && lastWrite) {
    const noun = writesCount === 1 ? "write" : "writes";
    return `${writesCount} ${noun} · ${lastWrite}`;
  }
  if (writesCount > 0) {
    const noun = writesCount === 1 ? "write" : "writes";
    return `${writesCount} confirmed ${noun}`;
  }
  return "Connected · ready for write-back";
};

/**
 * Normalize CRM status payloads from the API into a stable object.
 *
 * @example
 * normalizeCrmConnectorStatus({ connected: true, writes_count: "2" });
 * // { connected: true, credential_id: null, writes_count: 2, last_written_at: null }
 */
export const normalizeCrmConnectorStatus = (raw = {}) => ({
  connected: Boolean(raw.connected),
  credential_id: raw.credential_id ? String(raw.credential_id) : null,
  writes_count: Number(raw.writes_count) || 0,
  last_written_at: raw.last_written_at ? String(raw.last_written_at) : null,
});

/**
 * Build an empty CRM status map for all catalog CRM connectors.
 *
 * @example
 * buildEmptyCrmStatusById();
 * // { attio: { connected: false, ... }, salesforce: { connected: false, ... } }
 */
export const buildEmptyCrmStatusById = () => ({
  attio: normalizeCrmConnectorStatus(),
  salesforce: normalizeCrmConnectorStatus(),
});

/**
 * Derive connected CRM ids from a status map.
 *
 * @example
 * getConnectedCrmIdsFromStatus({ attio: { connected: true }, salesforce: { connected: false } });
 * // ["attio"]
 */
export const getConnectedCrmIdsFromStatus = (statusById = {}) =>
  Object.entries(statusById)
    .filter(([, status]) => Boolean(status?.connected))
    .map(([crmId]) => String(crmId));
